import { Button } from "@/components/ui/button";
import { ProductImageDisplay } from "@/components/product/ProductImageDisplay";
import { motion, useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, ArrowRight, Loader2, ShoppingCart } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useCart } from "@/contexts/CartContext";
import { useToast } from "@/hooks/use-toast";
import { staticBestsellers } from "@/data/staticProducts";

// Placeholder image for products without images
import placeholderImg from "@/assets/products/lineup1.png";

interface Product {
  id: string;
  name: string;
  category: string;
  price: number;
  image_url: string | null;
}

interface ProductCardProps {
  product: Product;
  index: number;
  onHover: () => void;
  onLeave: () => void;
  onAddToCart: (product: Product) => void;
}

// Different heights and offsets for visual variety
const cardStyles = [
  { height: "h-[280px] sm:h-[320px]", offset: "mt-0" },
  { height: "h-[360px] sm:h-[420px]", offset: "mt-16 sm:mt-24" },
  { height: "h-[300px] sm:h-[350px]", offset: "mt-4" },
  { height: "h-[380px] sm:h-[450px]", offset: "mt-20 sm:mt-28" },
  { height: "h-[260px] sm:h-[300px]", offset: "mt-2" },
  { height: "h-[340px] sm:h-[400px]", offset: "mt-14 sm:mt-20" },
];

function formatPrice(kobo: number) {
  return `₦${(kobo / 100).toLocaleString()}`;
}

function ProductCard({ product, index, onHover, onLeave, onAddToCart }: ProductCardProps) {
  const style = cardStyles[index % cardStyles.length];
  
  return (
    <Link
      to={`/products/${product.id}`}
      className={`flex-shrink-0 w-[260px] sm:w-[300px] ${style.offset} group cursor-pointer block`}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
    >
      {/* Card Image with Decorative Display */}
      <div className={`${style.height} relative overflow-hidden p-3`}>
        <ProductImageDisplay
          imageUrl={product.image_url || placeholderImg}
          alt={product.name}
          showBadge={true}
          badgeText={product.category}
          className="h-full"
        />
        {/* Hover Overlay */}
        <div className="absolute inset-3 z-20 rounded-2xl bg-primary/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center gap-3 backdrop-blur-sm">
          <Button
            variant="hero"
            size="default"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onAddToCart(product);
            }}
          >
            <ShoppingCart className="h-4 w-4 mr-2" />
            Add to Cart
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="bg-white/90 hover:bg-white"
            asChild
            onClick={(e) => e.stopPropagation()}
          >
            <a
              href={`https://api.whatsapp.com/send?phone=2347065732481&text=Hi! I'm interested in your ${product.name}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              WhatsApp
            </a>
          </Button>
        </div>
      </div>
      
      {/* Card Text Below Image */}
      <div className="mt-4 space-y-2">
        <h3 className="font-semibold text-foreground text-lg group-hover:text-primary transition-colors">
          {product.name}
        </h3>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {formatPrice(product.price)}
        </p>
      </div>
    </Link>
  );
}

export function FeaturedProducts() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [isPaused, setIsPaused] = useState(false);
  // Initialize with static bestsellers for immediate rendering (SEO)
  const [products, setProducts] = useState<Product[]>(staticBestsellers as Product[]);
  const [isLoading, setIsLoading] = useState(true);
  const { addItem } = useCart();
  const { toast } = useToast();

  const handleAddToCart = (product: Product) => {
    addItem({
      id: product.id,
      name: product.name,
      price: product.price,
      image_url: product.image_url || "",
      category: product.category,
    });
    toast({
      title: "Added to cart",
      description: `${product.name} has been added to your cart.`,
    });
  };

  // Curated bestseller product IDs
  const bestsellersIds = [
    '95ae67fe-90d9-4926-aa9d-635ffc3da041', // Acwell Licorice Ph Balancing Cleansing Toner 150ml
    '728fbdad-3ff1-4861-bd34-582c5c3fc129', // Tiam Vita B3 Source 40ml
    '5a04e42d-42a3-4309-a83f-eabbd1e037e1', // Kojie San
    '8c27e38c-6e31-479e-b305-06bd2905b08e', // Cosrx Advanced Snail 96 Mucin Power Essence
    '4864ccf8-658b-4273-944a-5390a2659513', // Cosrx Advanced Snail 92 All In One Cream
    '52fca10d-b357-4106-b786-392e38e4a44b', // Skin Aqua Spf 50+ Pa+++110g
    '175771ac-d332-40bb-b6c2-6203ad444f7d', // Anua Niacinamide 10% + TXA 4% Serum 30ml
  ];

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        // Fetch only the curated bestseller products
        const { data, error } = await supabase
          .from("products")
          .select("id, name, category, price, image_url")
          .in("id", bestsellersIds)
          .eq("in_stock", true)
          .eq("is_active", true);

        if (error) throw error;
        // Replace static data with fresh data if available
        if (data && data.length > 0) {
          setProducts(data);
        }
      } catch (error) {
        console.error("Error fetching featured products:", error);
        // Keep static products as fallback - no error shown to user
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, []);
  
  // Duplicate products for seamless infinite scroll
  const duplicatedProducts = [...products, ...products, ...products];

  return (
    <section ref={ref} className="section-padding bg-background overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-12"
        >
          <span className="inline-block text-sm font-medium tracking-widest uppercase text-primary mb-4">
            Shop Our Collection
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-foreground mb-6">
            Best <span className="text-gradient">Sellers</span>
          </h2>
          <p className="text-muted-foreground text-lg">
            Discover our handpicked selection of authentic skincare essentials, 
            curated to address your unique skin concerns.
          </p>
        </motion.div>
      </div>

      {/* Horizontal Scrolling Carousel */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : {}}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="relative w-full"
      >
        <style>
          {`
            @keyframes scrollProducts {
              0% { transform: translateX(0); }
              100% { transform: translateX(-33.33%); }
            }
          `}
        </style>
        {/* Fade edges */}
        <div className="absolute left-0 top-0 bottom-0 w-20 sm:w-32 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-20 sm:w-32 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />
        
        {/* Loading or Content - show content immediately with static data */}
        {isLoading && products.length === 0 ? (
          <div className="flex justify-center py-32">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-32">
            <p className="text-muted-foreground">No products available yet.</p>
          </div>
        ) : (
          /* Scrolling container */
          <div 
            className="overflow-hidden py-4 min-h-[500px] sm:min-h-[600px]"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            <div
              className="inline-flex gap-6 sm:gap-8 items-start pl-8"
              style={{
                animation: "scrollProducts 25s linear infinite",
                animationPlayState: isPaused ? "paused" : "running",
                willChange: "transform",
              }}
            >
              {duplicatedProducts.map((product, index) => (
                <ProductCard
                  key={`${product.id}-${index}`}
                  product={product}
                  index={index}
                  onHover={() => setIsPaused(true)}
                  onLeave={() => setIsPaused(false)}
                  onAddToCart={handleAddToCart}
                />
              ))}
            </div>
          </div>
        )}
      </motion.div>

      {/* View All Button */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="text-center mt-8"
        >
          <Button variant="outline" size="lg" asChild>
            <Link to="/products" className="inline-flex items-center gap-2">
              View All Products
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>
  );
}
